import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { BrandSummary, Tag } from "@shared/types";
import { buildTagIntro } from "@shared/seoContent";
import { api } from "../lib/api";
import { BrandGrid } from "../components/BrandGrid";
import { EmptyState } from "../components/EmptyState";
import { isAbortError } from "../lib/asyncState";

export function CategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const [categories, setCategories] = useState<Tag[]>([]);
  const [brands, setBrands] = useState<BrandSummary[]>([]);
  const [categoryTotal, setCategoryTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.categories.list({ occupied: true }).then((r) => setCategories(r.categories));
  }, []);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api.brands.list({ category: slug, sort: "crowd", limit: 48 }, { signal: controller.signal })
      .then((r) => {
        setBrands(r.items);
        setCategoryTotal(r.total);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setBrands([]);
        setCategoryTotal(0);
        setError("Could not load this category right now.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [slug, reloadKey]);

  if (!slug) {
    const primary = categories
      .filter((c) => c.type === "primary_category")
      .sort((a, b) => (b.brandCount ?? 0) - (a.brandCount ?? 0) || a.name.localeCompare(b.name));
    return (
      <div className="container" style={{ paddingBlock: "2rem" }}>
        <h2>Categories</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "0.75rem", marginTop: "1.5rem" }}>
          {primary.map((c) => (
            <Link key={c.id} to={`/categories/${c.slug}`} className="brand-card" style={{ padding: "1rem" }}>
              <div className="brand-card__name">{c.name}</div>
              <div className="meta" style={{ marginTop: "0.5rem" }}>
                {c.brandCount === 1 ? "1 brand" : `${c.brandCount ?? 0} brands`}
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  const current = categories.find((c) => c.slug === slug);
  const title = current?.name ?? formatSlug(slug ?? "");
  return (
    <div className="container" style={{ paddingBlock: "2rem" }}>
      <Link to="/categories" className="meta" style={{ display: "inline-block", marginBottom: "0.75rem" }}>
        All categories
      </Link>
      <div className="section__head">
        <h2>{title}</h2>
        <div style={{ color: "var(--fg-subtle)", fontSize: "0.9rem" }}>
          {categoryTotal === 1 ? "1 brand" : `${categoryTotal} brands`}
        </div>
      </div>
      {current && brands.length > 0 && (
        <p className="taxonomy-intro">{buildTagIntro(current, brands, "category")}</p>
      )}
      {loading ? (
        <div className="empty-state">Loading...</div>
      ) : error ? (
        <EmptyState title="Category could not load" actions={<button className="primary" onClick={() => setReloadKey((key) => key + 1)}>Try again</button>}>
          {error}
        </EmptyState>
      ) : brands.length ? (
        <BrandGrid brands={brands} />
      ) : (
        <EmptyState
          title="No brands in this category yet"
          actions={
            <>
            <Link to="/categories"><button className="primary">View active categories</button></Link>
            <Link to="/brands"><button>Browse brands</button></Link>
            </>
          }
        >
          This category is not active on the public radar right now.
        </EmptyState>
      )}
    </div>
  );
}

function formatSlug(value: string): string {
  return value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
